import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { isMuted, playComplete, playThunk, playWhoosh, releaseAudio, setMuted } from '../bbSound';
import { cdnBoxForCertCount, cdnIconForSlug, cdnSpineTexture } from '../bundleBuilderConfig';
import { Quad, applyH, matrix3d, slotBounds, solveHomography } from '../homography';

/**
 * The animated box set: textbooks slide into their slots over the box photograph.
 *
 * Ported from the marketing builder at traineracademy.org/bundle-builder. The whole effect is one
 * perspective projection — a flat 1024px stage is mapped onto the box's slot cavity with a CSS
 * matrix3d, so ordinary rectangular images land in the box with correct perspective and each book
 * keeps its own real slot.
 *
 * Everything here is CSS transforms, Web Audio and pure maths; no animation library, no Tailwind,
 * and the only assets are on the CDN the marketing page already serves.
 */

const STAGE = 1024;

/** Chrome accent — follows the active course via the CSS var set on the popup root. */
const ACCENT = 'var(--accent, #ED5A26)';
/** Gap between the box and its caption. Shared by the CSS and the measuring effect. */
const TITLE_GAP = 6;
const ENTER_MS = 400;
const EXIT_MS = 280;

/**
 * Slot-opening geometry, measured from the 1024px box renders and keyed by how many certifications
 * the box holds.
 *
 * Keyed by COUNT rather than tier name deliberately: the box photograph is chosen by member count,
 * so a bundle mis-tagged in admin (an Elite carrying two certifications) still gets geometry that
 * matches the picture it is drawn on instead of a quad for a box that is not there.
 *
 * `quad` is the cavity opening — the sub-pixel crossing where the lit top wall falls into shadow,
 * and where shadow rises back into the lit bottom wall. `dividers` are the screen-space x positions
 * of the walls between slots, so an unfilled slot stays visibly empty with its divider showing.
 */
const GEOMETRY: Record<number, { quad: Quad; dividers: number[] }> = {
  6: {
    quad: { x0: 483, x1: 874, yTL: 117, yTR: 157, yBR: 864, yBL: 916 },
    dividers: [551, 619, 685, 748, 808],
  },
  4: {
    quad: { x0: 474, x1: 836, yTL: 120, yTR: 151, yBR: 868, yBL: 913 },
    dividers: [560, 652, 738],
  },
  2: {
    quad: { x0: 545, x1: 763, yTL: 113, yTR: 140, yBR: 891, yBL: 931 },
    dividers: [651],
  },
};

/**
 * Spine textures are position-baked crops of the hero photograph, so each carries whatever shadow
 * fell on that book where it stood. Strength & Conditioning was shot last in the row — hard against
 * Group Exercise on its left and the case wall on its right — so it alone has near-black bands down
 * both edges. In any other slot those read as black gaps between books, so they are re-covered with
 * plain board lifted from the same texture.
 */
const EDGE_PATCH: Record<string, { left: number; right: number }> = {
  csc: { left: 0.11, right: 0.18 },
};

/**
 * A textless strip of board — x 0.17–0.31 is clean board on all six spines. A percentage
 * background-position aligns the image's p% point with the box's p%, so showing f0..f1 needs
 * p = f0 / (1 - span), not a plain offset.
 */
const BOARD_FILL = {
  backgroundRepeat: 'no-repeat' as const,
  backgroundSize: `${(100 / 0.14).toFixed(2)}% 100%`,
  backgroundPosition: `${((0.17 / 0.86) * 100).toFixed(2)}% 0`,
};

const patchFade = (dir: 'left' | 'right') => `linear-gradient(to ${dir}, #000 0, #000 55%, transparent 100%)`;

/** Uniform physical icon size, converted per slot through the projection so books match across tiers. */
const ICON_SCREEN_WIDTH = 50;
const ICON_BASELINE_FRAC = 0.885;
const ICON_ASPECT = 86 / 128;

/** Bigger box, bigger fanfare. */
const completeLevelFor = (certCount: number): 1 | 2 | 3 => (certCount >= 6 ? 3 : certCount >= 4 ? 2 : 1);

type BookStatus = 'entering' | 'in' | 'exiting';

interface Book {
  slug: string;
  status: BookStatus;
}

type Props = {
  /** Ordered slugs currently in the box; index is the slot the book sits in. */
  slugs: string[];
  /** Slots in this box — picks the photograph and the geometry. */
  certCount: number;
  /** True when every slot is filled; drives the celebration. */
  complete: boolean;
  /** Shown under the box. */
  title: string;
};

export const BundleBoxSet = ({ slugs, certCount, complete, title }: Props) => {
  const geo = GEOMETRY[certCount];

  const projection = useMemo(() => {
    if (!geo) return null;
    const solved = solveHomography(geo.quad);
    return { matrix: matrix3d(solved), bounds: slotBounds(solved, geo.dividers), h: solved };
  }, [geo]);

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLDivElement | null>(null);
  /** The box's side length in px — see the measuring effect. */
  const [side, setSide] = useState(0);
  const [muted, setMutedState] = useState(true);
  const [books, setBooks] = useState<Book[]>(() => slugs.map((slug) => ({ slug, status: 'in' })));
  const [bump, setBump] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  const [softGlow, setSoftGlow] = useState(false);

  const firstRender = useRef(true);
  const prevComplete = useRef(complete);
  const prevCount = useRef(certCount);
  /**
   * The catalogue arrives after mount, so the box goes from empty to full on its own.
   *
   * That first fill still animates — it makes a good reveal as the modal opens — but it must be
   * SILENT: the learner clicked Upgrade, not a certification, and a whoosh they did not ask for is
   * startling. Sound belongs to deliberate picks only.
   */
  const hasPopulated = useRef(false);
  /** Diffed in the effect body rather than inside a state updater — see the effect below. */
  const prevSlugs = useRef<string[]>(slugs);
  /** Set by the package effect to tell the completion effect that this commit is not an achievement. */
  const packageChangeClaimed = useRef(false);

  /**
   * Sizes the box to the smaller of the width and the height it has been given.
   *
   * Pure CSS cannot express "a square that fits inside this rectangle": deriving the height from
   * the width overflows a short row, deriving the width from the height overflows a narrow column,
   * and an aspect-ratio with a max on the opposite axis narrows the card without shortening it — a
   * mismatch that showed as the card's grey backing under the render. Measuring both axes and
   * setting an explicit square is the only version that cannot clip its neighbours, which is what
   * was cutting the bottom border off the stacks panel beneath it.
   *
   * A layout effect, so the first paint is already correct; a zero measurement is ignored so a
   * modal that mounts hidden does not collapse the box to nothing.
   */
  useLayoutEffect(() => {
    setMutedState(isMuted());
    const el = wrapRef.current;
    if (!el) return;

    const measure = () => {
      const width = el.clientWidth;
      // offsetHeight excludes the caption's own top margin, so that has to come off too or the box
      // ends up TITLE_GAP too tall and clips whatever sits under it by exactly that much.
      const available = el.clientHeight - (titleRef.current?.offsetHeight ?? 0) - TITLE_GAP;

      // A height of zero means the row is sizing to its content rather than constraining us — the
      // stacked mobile layout does this. Falling back to the width breaks the circularity; without
      // it the box measures 0, never grows, and never appears.
      const next = available > 0 ? Math.min(width, available) : width;
      if (next > 0) setSide(next);
    };

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();

    return () => {
      ro.disconnect();
      // The learner can open and close this modal all day; the audio context must not outlive it.
      releaseAudio();
    };
  }, []);

  /**
   * Reconcile the incoming selection into the book lifecycle and choreograph the sound: the whoosh
   * starts with the slide, the thunk lands 70ms before the book seats.
   *
   * The diff is taken here rather than inside the setBooks updater, and the updater is left pure.
   * React invokes updaters twice under StrictMode to surface exactly this kind of impurity, which
   * would double every whoosh and schedule two thunk timers per pick.
   */
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      prevSlugs.current = slugs;
      return;
    }

    const before = prevSlugs.current;
    prevSlugs.current = slugs;

    const added = slugs.filter((s) => !before.includes(s));
    const removed = before.filter((s) => !slugs.includes(s));
    if (added.length === 0 && removed.length === 0) return;

    const isFirstFill = !hasPopulated.current && before.length === 0;
    if (added.length > 0) hasPopulated.current = true;

    setBooks((current) => {
      if (isFirstFill) return added.map((slug) => ({ slug, status: 'entering' as const }));

      let next: Book[] = current.map((b) => (removed.includes(b.slug) ? { ...b, status: 'exiting' as const } : b));
      for (const slug of added) {
        next = next.filter((b) => b.slug !== slug);
        next.push({ slug, status: 'entering' });
      }
      return next;
    });

    // The opening reveal is deliberately silent.
    if (isFirstFill) return;

    const timers: number[] = [];
    if (added.length > 0) {
      playWhoosh('in');
      timers.push(
        window.setTimeout(() => {
          playThunk();
          setBump(true);
          timers.push(window.setTimeout(() => setBump(false), 220));
        }, ENTER_MS - 70),
      );
    } else if (removed.length > 0) {
      playWhoosh('out');
    }

    return () => timers.forEach(window.clearTimeout);
  }, [slugs]);

  // Promote entering -> in on the next frame so the browser paints the off-stage position first and
  // actually animates the transition; drop exiting books once their transition has run.
  useEffect(() => {
    if (books.some((b) => b.status === 'entering')) {
      const raf = requestAnimationFrame(() =>
        requestAnimationFrame(() =>
          setBooks((cur) => cur.map((b) => (b.status === 'entering' ? { ...b, status: 'in' } : b))),
        ),
      );
      return () => cancelAnimationFrame(raf);
    }
    if (books.some((b) => b.status === 'exiting')) {
      const timer = window.setTimeout(() => setBooks((cur) => cur.filter((b) => b.status !== 'exiting')), EXIT_MS);
      return () => window.clearTimeout(timer);
    }
  }, [books]);

  /**
   * Completion celebration, scaled to the package.
   *
   * Skipped whenever the package effect below has already claimed this commit — either the box was
   * filling for the first time, or the learner switched package. Both flip `complete` to true
   * without anyone having completed anything, and the switch has its own understated chime; firing
   * here as well stacks two celebrations into one blast.
   *
   * The claim is an explicit flag rather than a comparison against prevCount, because that effect
   * is a layout effect and therefore always runs BEFORE this one — any state it touches already
   * looks settled by the time this reads it.
   */
  useEffect(() => {
    const was = prevComplete.current;
    prevComplete.current = complete;

    if (packageChangeClaimed.current) {
      packageChangeClaimed.current = false;
      return;
    }

    if (complete && !was && !firstRender.current) {
      setJustCompleted(true);
      const chime = window.setTimeout(() => playComplete(completeLevelFor(certCount)), ENTER_MS);
      const clear = window.setTimeout(() => setJustCompleted(false), 2200);
      return () => {
        window.clearTimeout(chime);
        window.clearTimeout(clear);
      };
    }
  }, [complete, certCount]);

  /**
   * Package switch: re-seat every book instantly. Moving up to the full box gets a single light
   * confirmation chime and a brief soft glow — deliberately understated.
   *
   * Layout effect, because the parent changes package and picks in one commit: this component
   * renders once with the NEW geometry but the OLD book list, and six stale books clamp into a
   * two-slot box. A passive effect corrects that only after the browser has painted the pile.
   */
  useLayoutEffect(() => {
    const from = prevCount.current;
    prevCount.current = certCount;
    if (from === certCount) return;

    // Tell the completion effect to stand down: `complete` is about to flip for a reason the
    // learner did not cause.
    packageChangeClaimed.current = true;

    // from === 0 means there was no package before this: the catalogue has just arrived and the box
    // is filling for the first time. That is not a switch — leave the reveal above to play out, and
    // do not celebrate something the learner did not do.
    if (!from) return;

    setBooks(slugs.map((slug) => ({ slug, status: 'in' })));

    // Claim this change so the diff effect below sees nothing to do. A package switch replaces the
    // whole set at once; letting it also run the per-book path would fire a whoosh for books that
    // never visibly slide, on top of the switch's own chime.
    prevSlugs.current = slugs;
    hasPopulated.current = true;

    if (!firstRender.current && from !== certCount && certCount >= 6) {
      setSoftGlow(true);
      const chime = window.setTimeout(() => playComplete(1), 150);
      const clear = window.setTimeout(() => setSoftGlow(false), 1200);
      return () => {
        window.clearTimeout(chime);
        window.clearTimeout(clear);
      };
    }
    // Deliberately keyed on certCount alone: slugs are re-seated wholesale on a package change, and
    // reacting to them here would fight the lifecycle effect above, which owns per-book animation.
  }, [certCount]);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
  };

  const boxImage = cdnBoxForCertCount(certCount);

  /** An exiting book keeps its last slot so it slides out of where it sat. */
  const slotOf = (slug: string) => {
    const idx = slugs.indexOf(slug);
    return idx === -1 ? Math.max(0, slugs.length - 1) : idx;
  };

  const scale = side / STAGE;

  return (
    <Wrapper ref={wrapRef}>
      <BoxCard
        style={{ width: side || undefined, height: side || undefined }}
        $glow={justCompleted}
        $softGlow={softGlow}
        $bump={bump}>
        <Stage style={{ width: STAGE, height: STAGE, transform: `scale(${scale})`, transformOrigin: '0 0' }}>
          {boxImage && (
            <BoxPhoto src={boxImage} alt={`${title} box set`} width={STAGE} height={STAGE} draggable={false} />
          )}

          {projection && (
            <BookLayer
              style={{
                width: STAGE,
                height: STAGE,
                transform: projection.matrix,
                transformOrigin: '0 0',
              }}>
              {books.map((book) => {
                const slot = slotOf(book.slug);
                const u0 = projection.bounds[Math.min(slot, projection.bounds.length - 2)];
                const u1 = projection.bounds[Math.min(slot + 1, projection.bounds.length - 1)];
                const seated = book.status === 'in';
                const texture = cdnSpineTexture(book.slug);
                const patch = EDGE_PATCH[book.slug];

                // The icon is drawn at a constant on-screen size, which means converting through the
                // projection: horizontal and vertical plane scales differ, so height cannot simply
                // follow width.
                const vb = ICON_BASELINE_FRAC * STAGE;
                const [sx0] = applyH(projection.h, u0 * STAGE, vb);
                const [sx1] = applyH(projection.h, u1 * STAGE, vb);
                const iconWidthPct = (ICON_SCREEN_WIDTH / (sx1 - sx0)) * 100;
                const uc = ((u0 + u1) / 2) * STAGE;
                const [, syA] = applyH(projection.h, uc, vb);
                const [, syB] = applyH(projection.h, uc, vb - 100);
                const iconHeight = (ICON_SCREEN_WIDTH * ICON_ASPECT) / ((syA - syB) / 100);

                return (
                  <Slot key={book.slug} style={{ left: u0 * STAGE, width: (u1 - u0) * STAGE, top: 0, height: STAGE }}>
                    <BookInner
                      style={{
                        transform: seated
                          ? 'translate(0, 0) scale(1) rotate(0deg)'
                          : 'translate(85%, -4%) scale(1.14) rotate(2.5deg)',
                        opacity: seated ? 1 : 0.55,
                        filter: seated
                          ? 'drop-shadow(0 0 0 rgba(0,0,0,0))'
                          : 'drop-shadow(-18px 22px 22px rgba(0,0,0,0.35))',
                        transition: `transform ${
                          book.status === 'exiting' ? EXIT_MS : ENTER_MS
                        }ms cubic-bezier(0.25, 1, 0.4, 1), opacity 180ms ease, filter ${ENTER_MS}ms ease`,
                      }}>
                      <Spine src={texture} alt="" draggable={false} />

                      {patch && (
                        <>
                          <EdgePatch
                            aria-hidden
                            style={{
                              left: 0,
                              width: `${patch.left * 100}%`,
                              backgroundImage: `url("${texture}")`,
                              ...BOARD_FILL,
                              maskImage: patchFade('right'),
                              WebkitMaskImage: patchFade('right'),
                            }}
                          />
                          <EdgePatch
                            aria-hidden
                            style={{
                              right: 0,
                              width: `${patch.right * 100}%`,
                              backgroundImage: `url("${texture}")`,
                              ...BOARD_FILL,
                              maskImage: patchFade('left'),
                              WebkitMaskImage: patchFade('left'),
                            }}
                          />
                        </>
                      )}

                      <IconRow aria-hidden style={{ bottom: `${(1 - ICON_BASELINE_FRAC) * 100}%` }}>
                        <IconMark
                          src={cdnIconForSlug(book.slug)}
                          alt=""
                          draggable={false}
                          style={{ width: `${iconWidthPct}%`, height: `${iconHeight}px` }}
                        />
                      </IconRow>

                      {/* The inner wall casts a shade across the left of each seated book. */}
                      <Shade
                        aria-hidden
                        style={{ opacity: seated ? 1 : 0, transition: `opacity ${ENTER_MS}ms ease` }}
                      />
                    </BookInner>
                  </Slot>
                );
              })}

              {/* Contact shadow where the books meet the floor of the case. */}
              <Ground aria-hidden />
            </BookLayer>
          )}
        </Stage>

        <MuteButton
          type="button"
          onClick={toggleMute}
          aria-label={muted ? 'Unmute sound effects' : 'Mute sound effects'}>
          {muted ? (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          ) : (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
          )}
        </MuteButton>
      </BoxCard>

      <Title ref={titleRef} $lit={complete}>
        {title}
      </Title>
    </Wrapper>
  );
};

const glowPulse = keyframes`
  0%   { box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15); }
  25%  { box-shadow: 0 0 40px 8px var(--accent-strong, rgba(237, 90, 38, 0.55)), 0 10px 25px rgba(0, 0, 0, 0.15); }
  100% { box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15); }
`;

const glowPulseSoft = keyframes`
  0%   { box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15); }
  30%  { box-shadow: 0 0 22px 4px var(--accent-mid, rgba(237, 90, 38, 0.32)), 0 10px 25px rgba(0, 0, 0, 0.15); }
  100% { box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15); }
`;

const seatBump = keyframes`
  0%   { transform: translateY(0); }
  40%  { transform: translateY(3px); }
  100% { transform: translateY(0); }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 0;
`;

const BoxCard = styled.div<{ $glow: boolean; $softGlow: boolean; $bump: boolean }>`
  position: relative;
  /* Size comes from the measuring effect above — always square, always inside its box. */
  flex: 0 0 auto;
  overflow: hidden;
  border-radius: 16px;
  background: #e9e8e6;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);

  ${({ $glow, $softGlow }) =>
    $glow
      ? css`
          animation: ${glowPulse} 2s ease-out;
        `
      : $softGlow
      ? css`
          animation: ${glowPulseSoft} 1.1s ease-out;
        `
      : ''}

  ${({ $bump }) =>
    $bump &&
    css`
      animation: ${seatBump} 0.2s ease-out;
    `}

  /* Motion is decoration here — the box still shows the right books without it. */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Stage = styled.div`
  position: absolute;
  left: 0;
  top: 0;
`;

const BoxPhoto = styled.img`
  display: block;
`;

const BookLayer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
`;

const Slot = styled.div`
  position: absolute;
  /* Books slide sideways when a removal frees an earlier slot. */
  transition: left ${EXIT_MS}ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const BookInner = styled.div`
  height: 100%;
  width: 100%;

  @media (prefers-reduced-motion: reduce) {
    transition: none !important;
  }
`;

const Spine = styled.img`
  height: 100%;
  width: 100%;
  object-fit: fill;
  user-select: none;
`;

const EdgePatch = styled.div`
  pointer-events: none;
  position: absolute;
  top: 0;
  bottom: 0;
`;

const IconRow = styled.div`
  pointer-events: none;
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
`;

const IconMark = styled.img`
  filter: drop-shadow(-2px 3px 3px rgba(0, 0, 0, 0.55));
`;

const Shade = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.18), rgba(0, 0, 0, 0) 10%);
`;

const Ground = styled.div`
  pointer-events: none;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 5%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0));
`;

const MuteButton = styled.button`
  position: absolute;
  bottom: 12px;
  right: 12px;
  z-index: 10;
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.8);
  padding: 8px;
  line-height: 0;
  color: #141826;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;

  &:hover {
    background: #fff;
  }
`;

const Title = styled.div<{ $lit: boolean }>`
  /* Never shrinks: the box takes what it needs and would otherwise squeeze this out. */
  flex-shrink: 0;
  margin-top: ${TITLE_GAP}px;
  text-align: center;
  font-size: 18px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  transition: all 0.7s ease;
  color: ${({ $lit }) => ($lit ? ACCENT : '#9ca3af')};
  text-shadow: ${({ $lit }) =>
    $lit
      ? '0 0 18px var(--accent-strong, rgba(237, 90, 38, 0.55)), 0 0 40px var(--accent-glow, var(--accent-border, rgba(237, 90, 38, 0.25)))'
      : 'none'};
`;
